import { BackgroundAnimationAsset } from '@shared/models/background-animation.model';
import type * as csstree from 'css-tree';
import type sanitizeHtml from 'sanitize-html';

type CssTreeModule = typeof import('css-tree');
type SanitizeHtmlFn = typeof sanitizeHtml;

const BASE_DOCUMENT_STYLES = `
  html, body {
    width: 100%;
    height: 100%;
    margin: 0;
    overflow: hidden;
    background: transparent;
  }

  body {
    position: relative;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  .od-background-animation-root {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    isolation: isolate;
  }

  @media (prefers-reduced-motion: reduce) {
    .od-background-animation-root,
    .od-background-animation-root *,
    .od-background-animation-root *::before,
    .od-background-animation-root *::after {
      animation: none !important;
      transition: none !important;
    }
  }
`;

const FRAME_CSP = [
  `default-src 'none'`,
  `base-uri 'none'`,
  `form-action 'none'`,
  `connect-src 'none'`,
  `img-src data:`,
  `font-src data:`,
  `media-src 'none'`,
  `object-src 'none'`,
  `style-src 'unsafe-inline'`,
].join('; ');

const ALLOWED_HTML_TAGS = [
  'div',
  'span',
  'p',
  'section',
  'article',
  'header',
  'footer',
  'main',
  'aside',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'b',
  'em',
  'i',
  'small',
  'sup',
  'sub',
] as const;

const HTML_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: [...ALLOWED_HTML_TAGS],
  allowedAttributes: {
    '*': ['class', 'id', 'role', 'title'],
  },
  allowedSchemes: [],
  allowedSchemesAppliedToAttributes: ['href', 'src'],
  allowProtocolRelative: false,
  disallowedTagsMode: 'discard',
};

const UNSAFE_CSS_PATTERNS = [
  /javascript\s*:/i,
  /behavior\s*:/i,
  /-moz-binding\b/i,
];
const BLOCKED_CSS_AT_RULES = new Set(['import']);
const BLOCKED_CSS_FUNCTIONS = new Set(['url', 'expression']);

export async function buildBackgroundAnimationSrcdoc(
  animation: BackgroundAnimationAsset | null
): Promise<string> {
  if (!animation) {
    return '';
  }

  const trimmedHtml = (animation.html ?? '').trim();
  const trimmedCss = (animation.css ?? '').trim();

  if (!trimmedHtml && !trimmedCss) {
    return '';
  }

  const [cssTreeModule, sanitizeHtmlModule] = await Promise.all([
    trimmedCss ? import('css-tree') : null,
    trimmedHtml ? import('sanitize-html') : null,
  ]);

  const sanitizedHtml = sanitizeHtmlModule
    ? sanitizeAnimationHtml(sanitizeHtmlModule.default, trimmedHtml)
    : '';
  const sanitizedCss = cssTreeModule
    ? sanitizeAnimationCss(cssTreeModule, trimmedCss)
    : '';

  if (!sanitizedHtml && !sanitizedCss) {
    return '';
  }

  return [
    '<!DOCTYPE html>',
    '<html>',
    '<head>',
    '<meta charset="utf-8" />',
    '<meta name="viewport" content="width=device-width, initial-scale=1" />',
    `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}" />`,
    `<style>${BASE_DOCUMENT_STYLES}</style>`,
    `<style>${sanitizedCss}</style>`,
    '</head>',
    '<body>',
    `<div class="od-background-animation-root">${sanitizedHtml}</div>`,
    '</body>',
    '</html>',
  ].join('');
}

function sanitizeAnimationHtml(sanitize: SanitizeHtmlFn, html: string): string {
  if (!html) {
    return '';
  }

  return sanitize(html, HTML_SANITIZE_OPTIONS).trim();
}

function sanitizeAnimationCss(cssTree: CssTreeModule, css: string): string {
  if (!css) {
    return '';
  }

  if (UNSAFE_CSS_PATTERNS.some(pattern => pattern.test(css))) {
    return '';
  }

  try {
    const cssAst = cssTree.parse(css, { context: 'stylesheet' });

    return containsBlockedCssNodes(cssTree, cssAst) ? '' : css;
  } catch {
    return '';
  }
}

function containsBlockedCssNodes(
  cssTree: CssTreeModule,
  cssAst: csstree.CssNode
): boolean {
  let hasBlockedNode = false;

  cssTree.walk(cssAst, {
    enter(node: csstree.CssNode) {
      if (
        node.type === 'Atrule' &&
        BLOCKED_CSS_AT_RULES.has(node.name.toLowerCase())
      ) {
        hasBlockedNode = true;
        return cssTree.walk.break;
      }

      if (node.type === 'Url') {
        hasBlockedNode = true;
        return cssTree.walk.break;
      }

      if (
        node.type === 'Function' &&
        BLOCKED_CSS_FUNCTIONS.has(node.name.toLowerCase())
      ) {
        hasBlockedNode = true;
        return cssTree.walk.break;
      }

      if (
        node.type === 'Raw' &&
        UNSAFE_CSS_PATTERNS.some(pattern => pattern.test(node.value))
      ) {
        hasBlockedNode = true;
        return cssTree.walk.break;
      }
    },
  });

  return hasBlockedNode;
}
